/**
 * iter-360b — FeaturedBrandPlacement (aka SponsorBadge for back-compat).
 *
 * Renders a Featured Brand Partner tile. The visible label adapts to the
 * campaign's `partnership_kind`:
 *   featured_brand_partner   → "Featured Brand Partner"   (default)
 *   brand_spotlight          → "Brand Spotlight"          (library/marketplace)
 *   featured_product_partner → "Featured Product Partner" (product-specific)
 *   in_association_with      → "In Association With"      (project-level)
 *
 * iter-362 — The `rich-catalogue` variant (used on Vendor Publish) now
 * embeds an "Enquire" button that opens a lead-capture popover
 * (SponsorEnquireDialog). Submitting posts a `lead` event and opens the
 * sponsor site in a new tab.
 *
 * AI GUARDRAIL: pure UI component. Sponsor payload NEVER enters AI prompts.
 * Sponsors are surfaced only through /api/sponsors/placements.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { ExternalLink, MessageCircle } from 'lucide-react';
import SponsorEnquireDialog from '@/components/SponsorEnquireDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_LABEL = {
  featured_brand_partner: 'Featured Brand Partner',
  brand_spotlight: 'Brand Spotlight',
  featured_product_partner: 'Featured Product Partner',
  in_association_with: 'In Association With',
};

// iter-363 — Anonymous visitor id for public/share-link viewers so
// impression dedupe works across page refreshes. Persisted in localStorage
// under a stable key. Rotates only if the user clears storage.
function _getVisitorId() {
  try {
    let v = localStorage.getItem('bella_visitor_id');
    if (!v) {
      v = 'v-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem('bella_visitor_id', v);
    }
    return v;
  } catch { return ''; }
}

export default function SponsorBadge({
  zone, category = null, limit = 1, compact = false,
  projectId = null, worksheetId = null,
  variant = 'default', // 'default' | 'premium-minimal' | 'rich-catalogue'
  className = '',
  publicToken = null, // iter-363 — enables anon fetch via /public/sponsors/placements
}) {
  const [rows, setRows] = useState([]);
  const [enquireSponsor, setEnquireSponsor] = useState(null);

  useEffect(() => {
    if (!zone) return;
    const token = localStorage.getItem('token');
    // Public-token mode: don't require auth; hit the public endpoint.
    if (publicToken) {
      const vid = _getVisitorId();
      const params = new URLSearchParams({ token: publicToken, zone, limit: String(limit) });
      if (category) params.set('category', category);
      if (vid) params.set('visitor_id', vid);
      axios.get(`${API}/public/sponsors/placements?${params.toString()}`)
        .then((r) => setRows(r.data || []))
        .catch(() => setRows([]));
      return;
    }
    if (!token) return;
    const params = new URLSearchParams({ zone, limit: String(limit) });
    if (category) params.set('category', category);
    axios.get(`${API}/sponsors/placements?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]));
  }, [zone, category, limit, publicToken]);

  if (!rows || rows.length === 0) return null;

  const trackClick = (id) => {
    if (publicToken) {
      // Anonymous click track via public endpoint.
      axios.post(`${API}/public/sponsors/${id}/track`, {
        event: 'click', token: publicToken, context: zone, source_zone: zone,
        visitor_id: _getVisitorId(),
      }).catch(() => { /* silent */ });
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.post(`${API}/sponsors/${id}/track`, {
      event: 'click', context: zone, source_zone: zone,
      project_id: projectId, worksheet_id: worksheetId,
    }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => { /* silent */ });
  };

  const isPremiumMinimal = variant === 'premium-minimal';
  const isRich = variant === 'rich-catalogue';

  return (
    <div className={`space-y-2 ${className}`} data-testid={`sponsor-badge-${zone}`}>
      {rows.map((row) => {
        const kindLabel = KIND_LABEL[row.partnership_kind] || KIND_LABEL.featured_brand_partner;
        // For the rich (Vendor Publish) variant, use a div container so that
        // we can host BOTH the "Learn more" link AND a separate "Enquire"
        // button (nesting a button inside <a> is invalid HTML).
        if (isRich) {
          return (
            <div
              key={row.id}
              data-testid={`sponsor-item-${row.id}`}
              className="group block relative overflow-hidden rounded-lg border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50 hover:border-amber-400 hover:shadow-md p-3"
            >
              <span
                className="absolute top-1.5 right-1.5 text-[9px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 border text-amber-700 bg-amber-100 border-amber-300"
                data-testid={`sponsor-label-${row.id}`}
              >
                {kindLabel}
              </span>
              <div className="flex items-center gap-3">
                {row.logo_url ? (
                  <img src={row.logo_url} alt={row.brand_name}
                    className="object-contain flex-shrink-0 w-14 h-14"
                    onError={(e) => { e.target.style.display = 'none'; }} />
                ) : (
                  <div className="flex items-center justify-center rounded-md flex-shrink-0 font-bold w-14 h-14 bg-amber-100 text-amber-800 text-lg">
                    {(row.brand_name || '?').charAt(0)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-stone-900 truncate text-sm">
                    {row.brand_name}
                  </div>
                  {row.tagline && (
                    <div className="text-stone-600 line-clamp-2 text-xs">
                      {row.tagline}
                    </div>
                  )}
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <a
                      href={row.cta_url}
                      target="_blank"
                      rel="noopener noreferrer sponsored"
                      onClick={() => trackClick(row.id)}
                      data-testid={`sponsor-cta-${row.id}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-900 hover:underline"
                    >
                      {row.cta_label || 'Learn more'}
                      <ExternalLink size={11} />
                    </a>
                    <button
                      type="button"
                      onClick={() => setEnquireSponsor(row)}
                      data-testid={`sponsor-enquire-btn-${row.id}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 px-2 py-0.5 rounded-full transition-colors"
                    >
                      <MessageCircle size={11} />
                      Enquire
                    </button>
                  </div>
                </div>
                {row.banner_url && (
                  <img src={row.banner_url} alt="" className="hidden md:block w-24 h-16 object-cover rounded flex-shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
                )}
              </div>
              <div className="text-[9px] text-stone-400 mt-1 italic">
                Promoted Partnership · does not affect BOQ costing or specifications
              </div>
            </div>
          );
        }
        // Non-rich variants keep the original anchor-wrapped card layout.
        return (
          <a
            key={row.id}
            href={row.cta_url}
            target="_blank"
            rel="noopener noreferrer sponsored"
            onClick={() => trackClick(row.id)}
            data-testid={`sponsor-item-${row.id}`}
            className={`group block relative overflow-hidden rounded-lg border transition-all ${
              isPremiumMinimal
                ? 'border-stone-200 bg-white hover:border-amber-300 p-2.5'
                : 'border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50 hover:border-amber-400 hover:shadow-md ' + (compact ? 'p-2' : 'p-3')
            }`}
          >
            <span
              className={`absolute top-1.5 right-1.5 text-[9px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 border ${
                isPremiumMinimal
                  ? 'text-stone-600 bg-stone-50 border-stone-200'
                  : 'text-amber-700 bg-amber-100 border-amber-300'
              }`}
              data-testid={`sponsor-label-${row.id}`}
            >
              {kindLabel}
            </span>
            <div className={`flex items-center gap-3 ${isPremiumMinimal ? 'pr-24' : ''}`}>
              {row.logo_url ? (
                <img src={row.logo_url} alt={row.brand_name}
                  className={`object-contain flex-shrink-0 ${compact || isPremiumMinimal ? 'w-9 h-9' : 'w-14 h-14'}`}
                  onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <div className={`flex items-center justify-center rounded-md flex-shrink-0 font-bold ${
                  isPremiumMinimal ? 'w-9 h-9 bg-stone-100 text-stone-700 text-sm' : 'w-14 h-14 bg-amber-100 text-amber-800 text-lg'
                }`}>
                  {(row.brand_name || '?').charAt(0)}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className={`font-semibold text-stone-900 truncate ${compact || isPremiumMinimal ? 'text-xs' : 'text-sm'}`}>
                  {row.brand_name}
                </div>
                {row.tagline && !isPremiumMinimal && (
                  <div className={`text-stone-600 line-clamp-2 ${compact ? 'text-[10px]' : 'text-xs'}`}>
                    {row.tagline}
                  </div>
                )}
                <div className={`mt-0.5 inline-flex items-center gap-1 font-semibold group-hover:underline ${
                  isPremiumMinimal ? 'text-[10px] text-amber-700' : (compact ? 'text-[10px] text-amber-700' : 'text-xs text-amber-700 group-hover:text-amber-900')
                }`}>
                  {row.cta_label || 'Learn more'}
                  <ExternalLink size={compact || isPremiumMinimal ? 9 : 11} />
                </div>
              </div>
            </div>
            <div className="text-[9px] text-stone-400 mt-1 italic">
              Promoted Partnership · does not affect BOQ costing or specifications
            </div>
          </a>
        );
      })}
      {enquireSponsor && (
        <SponsorEnquireDialog
          open={!!enquireSponsor}
          onOpenChange={(v) => { if (!v) setEnquireSponsor(null); }}
          sponsorId={enquireSponsor.id}
          sponsorName={enquireSponsor.brand_name}
          sponsorCtaUrl={enquireSponsor.cta_url}
          projectId={projectId}
          worksheetId={worksheetId}
          sourceZone={zone}
          publicToken={publicToken}
        />
      )}
    </div>
  );
}
